import React from 'react';

export interface DetailGridItem {
  label: string,
  value: string | null, // already formatted by the caller; null shows emptyValue
}

interface DetailGridLayoutProps {
  columnWidth: number, // px; the width of each of the two columns
  className?: string,
}

export interface DetailGridProps extends DetailGridLayoutProps {
  items: DetailGridItem[],
  emptyValue?: string,
}

// The full household grid, so the skeleton is as tall as the card it stands in for
const skeletonRowIds = Array.from(Array(12).keys(), (index) => `skeleton-row-${index}`);

const gridClassName = 'grid gap-x-4 gap-y-2';

const getGridStyle = (columnWidth: number): React.CSSProperties => ({
  gridTemplateColumns: `repeat(2, ${columnWidth}px)`,
});

function DetailGridRow({ label, value }: Readonly<{ label: React.ReactNode, value: React.ReactNode }>) {
  return (
    <div className="flex flex-col py-2 border-b border-pipbg-subtle">
      <dt className="text-body-large text-piptext-strong">{label}</dt>
      <dd className="font-sans text-base leading-[22.4px] font-semibold text-piptext-default">{value}</dd>
    </div>
  );
}

// Label and value pairs, two per row, each with a divider under it.
// Display only: the caller formats the values.
export default function DetailGrid({
  items, emptyValue = 'No Data', columnWidth, className = '',
}: Readonly<DetailGridProps>) {
  if (items.length === 0) return null;

  return (
    <dl className={`${gridClassName} ${className}`} style={getGridStyle(columnWidth)}>
      {items.map(({ label, value }) => (
        <DetailGridRow key={label} label={label} value={value ?? emptyValue} />
      ))}
    </dl>
  );
}

function SkeletonBar({ widthClassName }: Readonly<{ widthClassName: string }>) {
  return <span className={`block rounded bg-pipgrey-light ${widthClassName}`}>&nbsp;</span>;
}

export function DetailGridSkeleton({ columnWidth, className = '' }: Readonly<DetailGridLayoutProps>) {
  return (
    <dl
      aria-hidden="true"
      className={`${gridClassName} ${className} animate-pulse motion-reduce:animate-none`}
      style={getGridStyle(columnWidth)}
    >
      {skeletonRowIds.map((rowId) => (
        <DetailGridRow
          key={rowId}
          label={<SkeletonBar widthClassName="w-2/3" />}
          value={<SkeletonBar widthClassName="w-1/3" />}
        />
      ))}
    </dl>
  );
}
